import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input, Select } from '../atoms';
import { Modal, useToast } from '../organisms';
import { EntityHeader } from '../shell';
import { useEntityPermission } from '../lib/usePermissions';

/**
 * InvoiceTermsDetail — /v2/invoice-terms/:id
 *
 * A focused fee-structure editor (Domain 04.2), replacing the generic detail
 * dump. Configure a client's arrangement once: contingency %, flat fee, or
 * hybrid (both), plus per-account (property_cap) and per-client (total_cap)
 * caps. `rate` is a decimal fraction in the DB (0.25 = 25%); the editor works
 * in whole percent and converts on read/write.
 */

type BillingType = 'contingency' | 'flat_fee' | 'hybrid' | 'reduction_contingency';

interface InvoiceTerm {
  id: string;
  client_id: string | null;
  property_id: string | null;
  year: number | null;
  name: string | null;
  billing_type: BillingType | null;
  rate: number | null;
  flat_amount: number | null;
  property_cap: number | null;
  total_cap: number | null;
  // A15 (157) — the agreement's "Less Prior Billings" tick. A General-terms input,
  // which is why it lives beside the caps rather than on the item.
  less_prior_billings?: boolean | null;
  // 204 (ATA part 3 §5b): tiers by savings or assessed value, the two-step authority value and the base fee.
  fee_tiers_json?: string | null;
  authority_value?: number | null;
  authority_rate?: number | null;
  base_fee_amount?: number | null;
}

type TierBasis = 'savings' | 'assessed_value';
interface FeeTier { threshold: number; rate: number }
interface FeeTiers { basis: TierBasis; tiers: FeeTier[] }
const TIER_BASIS_LABEL: Record<TierBasis, string> = { savings: 'savings', assessed_value: 'assessed value' };

function parseTiers(raw: string | null | undefined): FeeTiers | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as FeeTiers;
    if (!v || (v.basis !== 'savings' && v.basis !== 'assessed_value') || !Array.isArray(v.tiers) || v.tiers.length === 0) return null;
    return v;
  } catch { return null; }
}

interface ClientLite { id: string; name: string | null; short_name: string | null }

const billingTypeLabel: Record<BillingType, string> = {
  contingency: 'Contingency', flat_fee: 'Flat fee', hybrid: 'Hybrid',
  reduction_contingency: 'Reduction contingency',
};
// reduction_contingency bills a % of the raw value reduction (tax-rate-free), so it
// carries a rate just like plain contingency and hybrid.
const hasRate = (t: BillingType | null) =>
  t === 'contingency' || t === 'hybrid' || t === 'reduction_contingency';
const hasFlat = (t: BillingType | null) => t === 'flat_fee' || t === 'hybrid';

const money = (v: number | null) => (v != null ? `$${Math.round(v).toLocaleString()}` : '—');
const pct = (v: number | null) => (v != null ? `${+(v * 100).toFixed(4)}%` : '—');

interface RecomputeResult { term_id: string; recomputed: number; invoices_refreshed: number }

export function InvoiceTermsDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const canEdit = useEntityPermission('invoice_term', 'can_edit');

  const termQuery = useQuery<InvoiceTerm>({
    queryKey: ['invoice_term', id],
    queryFn: ({ signal }) => api.get<InvoiceTerm>(`/api/invoice-terms/${id}`, signal),
    enabled: !!id,
  });

  // 04.6 — explicitly re-propagate this term's rate to its dependent staged
  // (draft/pending) fee items via the real cascade endpoint. Frozen (sent/paid)
  // items are never touched server-side.
  const recompute = useMutation<RecomputeResult>({
    mutationFn: () => api.post<RecomputeResult>(`/api/billing/recompute-contingency/${id}`),
    onSuccess: (r) => {
      toast({
        variant: 'success',
        message: r.recomputed > 0
          ? `Recomputed ${r.recomputed} staged fee${r.recomputed === 1 ? '' : 's'} across ${r.invoices_refreshed} draft invoice${r.invoices_refreshed === 1 ? '' : 's'}.`
          : 'No staged fees follow this term — nothing to recompute.',
      });
      queryClient.invalidateQueries({ queryKey: ['invoices-list'] });
    },
    onError: (e) => {
      const b = e instanceof ApiClientError ? (e.body as { error?: string }) : undefined;
      toast({ variant: 'error', message: b?.error || (e as Error).message });
    },
  });

  const clientId = termQuery.data?.client_id ?? undefined;
  const clientQuery = useQuery<ClientLite>({
    queryKey: ['client', clientId],
    queryFn: ({ signal }) => api.get<ClientLite>(`/api/clients/${clientId}`, signal),
    enabled: !!clientId,
  });

  if (termQuery.isLoading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading fee terms…</p>;
  if (termQuery.error) {
    const notFound = termQuery.error instanceof ApiClientError && termQuery.error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>{notFound ? 'Fee terms not found' : 'Failed to load fee terms'}</h2>
        <p style={{ margin: 0 }}>{(termQuery.error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate('/invoice-terms')}>Back to Invoice Terms</Button>
        </p>
      </div>
    );
  }
  if (!termQuery.data) return null;

  const term = termQuery.data;
  const tiers = parseTiers(term.fee_tiers_json);
  const client = clientQuery.data;
  const clientName = client?.name || client?.short_name || null;

  return (
    <>
      <EntityHeader
        prefix="Fee terms"
        title={term.name || (term.billing_type ? `${billingTypeLabel[term.billing_type]} terms` : 'Fee terms')}
        subtitle={[clientName, term.year ? `Tax year ${term.year}` : null].filter(Boolean).join(' · ') || undefined}
        statusPills={term.billing_type ? <Badge variant="info" dot>{billingTypeLabel[term.billing_type]}</Badge> : undefined}
        actions={canEdit ? (
          <>
            <Button
              variant="secondary"
              size="sm"
              loading={recompute.isPending}
              onClick={() => recompute.mutate()}
            >
              Recompute staged fees
            </Button>
            <Button variant="primary" size="sm" onClick={() => setEditOpen(true)}>Edit fee structure</Button>
          </>
        ) : undefined}
      />

      <div style={{
        background: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-lg)',
        padding: 'var(--sp-5)',
        maxWidth: 520,
      }}>
        <h3 style={{
          margin: '0 0 var(--sp-4)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)',
        }}>Fee structure</h3>
        <Row label="Billing type" value={term.billing_type ? billingTypeLabel[term.billing_type] : '—'} />
        {hasRate(term.billing_type) && <Row label="Contingency rate" value={pct(term.rate)} />}
        {hasFlat(term.billing_type) && <Row label="Flat fee" value={money(term.flat_amount)} />}
        <Row label="Per-account cap" value={money(term.property_cap)} />
        <Row label="Per-client cap" value={money(term.total_cap)} />
        <Row
          label="Less prior billings"
          value={term.less_prior_billings ? 'On — fees bill the increment' : 'Off'}
        />
        {tiers && (
          <div data-testid="fee-tiers" style={{ padding: 'var(--sp-2) 0', fontSize: 'var(--fs-sm)', borderBottom: '1px solid var(--color-border)' }}>
            <div style={{ color: 'var(--color-ink-3)', marginBottom: 'var(--sp-1)' }}>Rate by {TIER_BASIS_LABEL[tiers.basis]}</div>
            {tiers.tiers.map((t, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
                <span>{i + 1 < tiers.tiers.length ? `${money(t.threshold)} to ${money(tiers.tiers[i + 1].threshold)}` : `${money(t.threshold)} and up`}</span>
                <span style={{ fontWeight: 'var(--fw-medium)' }}>{pct(t.rate)}</span>
              </div>
            ))}
            <div style={{ color: 'var(--color-ink-3)', marginTop: 'var(--sp-1)' }}>The tier the {TIER_BASIS_LABEL[tiers.basis]} reaches sets the rate for the whole amount.</div>
          </div>
        )}
        {term.authority_value != null && (
          <>
            <Row label="Authority value" value={money(term.authority_value)} />
            <Row label="Below authority" value={`${term.authority_rate != null ? pct(term.authority_rate) : 'the term rate'}, no cap`} />
          </>
        )}
        {term.base_fee_amount != null && <Row label="Base fee" value={money(term.base_fee_amount)} />}
        {clientName && (
          <p style={{ margin: 'var(--sp-4) 0 0', fontSize: 'var(--fs-sm)' }}>
            <a
              href={`/clients/${term.client_id}`}
              onClick={(e) => { e.preventDefault(); navigate(`/clients/${term.client_id}`); }}
              style={{ color: 'var(--brand-primary)', cursor: 'pointer' }}
            >View {clientName}</a>
          </p>
        )}
      </div>

      {editOpen && (
        <EditFeeStructure
          term={term}
          onClose={() => setEditOpen(false)}
        />
      )}
    </>
  );
}

function EditFeeStructure({ term, onClose }: { term: InvoiceTerm; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [billingType, setBillingType] = useState<BillingType>(term.billing_type || 'contingency');
  const [ratePct, setRatePct] = useState(term.rate != null ? String(+(term.rate * 100).toFixed(4)) : '');
  const [flat, setFlat] = useState(term.flat_amount != null ? String(term.flat_amount) : '');
  const [propertyCap, setPropertyCap] = useState(term.property_cap != null ? String(term.property_cap) : '');
  const [totalCap, setTotalCap] = useState(term.total_cap != null ? String(term.total_cap) : '');
  const [lessPrior, setLessPrior] = useState(term.less_prior_billings === true);
  // §5b: tiers edited in whole percent and dollars; stored as a fraction inside fee_tiers_json.
  const initialTiers = parseTiers(term.fee_tiers_json);
  const [tierBasis, setTierBasis] = useState<TierBasis | ''>(initialTiers ? initialTiers.basis : '');
  const [tierRows, setTierRows] = useState<Array<{ threshold: string; rate: string }>>(
    initialTiers ? initialTiers.tiers.map((t) => ({ threshold: String(t.threshold), rate: String(+(t.rate * 100).toFixed(4)) })) : [{ threshold: '0', rate: '' }],
  );
  const [authorityValue, setAuthorityValue] = useState(term.authority_value != null ? String(term.authority_value) : '');
  const [authorityRatePct, setAuthorityRatePct] = useState(term.authority_rate != null ? String(+(term.authority_rate * 100).toFixed(4)) : '');
  const [baseFee, setBaseFee] = useState(term.base_fee_amount != null ? String(term.base_fee_amount) : '');

  const tierProblem = (() => {
    if (!tierBasis) return null;
    let last = -1;
    for (const [i, r] of tierRows.entries()) {
      const th = Number(r.threshold); const rt = Number(r.rate);
      if (r.threshold.trim() === '' || !Number.isFinite(th) || th < 0) return `Tier ${i + 1} needs a threshold in dollars`;
      if (r.rate.trim() === '' || !Number.isFinite(rt) || rt < 0 || rt > 100) return `Tier ${i + 1} needs a rate between 0 and 100`;
      if (th <= last) return `Tier ${i + 1} must start above tier ${i}`;
      last = th;
    }
    return null;
  })();
  const setTier = (i: number, key: 'threshold' | 'rate', v: string) => setTierRows((rows) => rows.map((r, j) => (j === i ? { ...r, [key]: v } : r)));

  const num = (s: string): number | null => {
    const t = s.trim();
    if (t === '') return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        billing_type: billingType,
        // Only persist the field(s) the mode uses; clear the other so a switched
        // mode can't leave a stale rate/flat behind.
        rate: hasRate(billingType) ? (num(ratePct) != null ? (num(ratePct) as number) / 100 : null) : null,
        flat_amount: hasFlat(billingType) ? num(flat) : null,
        property_cap: num(propertyCap),
        total_cap: num(totalCap),
        less_prior_billings: lessPrior,
        fee_tiers_json: tierBasis && !tierProblem
          ? JSON.stringify({ basis: tierBasis, tiers: tierRows.map((r) => ({ threshold: Number(r.threshold), rate: Number(r.rate) / 100 })) })
          : null,
        authority_value: num(authorityValue),
        authority_rate: num(authorityRatePct) != null ? (num(authorityRatePct) as number) / 100 : null,
        base_fee_amount: num(baseFee),
      };
      return api.put(`/api/invoice-terms/${term.id}`, body);
    },
    onSuccess: () => {
      toast({ variant: 'success', message: 'Fee structure saved.' });
      queryClient.invalidateQueries({ queryKey: ['invoice_term', term.id] });
      onClose();
    },
    onError: (e) => {
      const b = e instanceof ApiClientError ? (e.body as { error?: string }) : undefined;
      toast({ variant: 'error', message: b?.error || (e as Error).message });
    },
  });

  return (
    <Modal open onClose={onClose} title="Edit fee structure" size="sm">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        <Field label="Billing type">
          <Select
            value={billingType}
            onChange={(e) => setBillingType(e.target.value as BillingType)}
            style={{ width: '100%' }}
            options={[
              { value: 'contingency', label: 'Contingency (% of tax saved)' },
              { value: 'reduction_contingency', label: 'Reduction contingency (% of value reduction)' },
              { value: 'flat_fee', label: 'Flat fee' },
              { value: 'hybrid', label: 'Hybrid (flat + %)' },
            ]}
          />
        </Field>
        {hasRate(billingType) && (
          <Field label="Contingency rate (%)">
            <Input type="number" value={ratePct} onChange={(e) => setRatePct(e.target.value)} placeholder="e.g. 25" style={{ width: '100%' }} />
          </Field>
        )}
        {hasFlat(billingType) && (
          <Field label="Flat fee ($)">
            <Input type="number" value={flat} onChange={(e) => setFlat(e.target.value)} placeholder="e.g. 1200" style={{ width: '100%' }} />
          </Field>
        )}
        <Field label="Per-account cap ($, optional)">
          <Input type="number" value={propertyCap} onChange={(e) => setPropertyCap(e.target.value)} placeholder="No cap" style={{ width: '100%' }} />
        </Field>
        <Field label="Per-client cap ($, optional)">
          <Input type="number" value={totalCap} onChange={(e) => setTotalCap(e.target.value)} placeholder="No cap" style={{ width: '100%' }} />
        </Field>
        <label style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'flex-start', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={lessPrior}
            onChange={(e) => setLessPrior(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span style={{ fontSize: 'var(--fs-sm)' }}>
            <span style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-ink-2)' }}>Less prior billings</span>
            <span style={{ display: 'block', color: 'var(--color-ink-3)' }}>
              When a later event bills the same client, property and year, charge only the
              increment it added — so the client is never billed twice for the same
              reduction. Off by default; leaving it off bills each event in full.
            </span>
          </span>
        </label>
        <Field label="Rate by tier">
          <Select
            value={tierBasis}
            onChange={(e) => setTierBasis(e.target.value as TierBasis | '')}
            style={{ width: '100%' }}
            options={[
              { value: '', label: 'No tiers (one rate)' },
              { value: 'savings', label: 'By savings' },
              { value: 'assessed_value', label: 'By assessed value' },
            ]}
          />
        </Field>
        {tierBasis && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
            {tierRows.map((r, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 'var(--sp-2)', alignItems: 'end' }}>
                <Field label={i === 0 ? 'From ($)' : ''}>
                  <Input type="number" aria-label={`Tier ${i + 1} threshold`} value={r.threshold} onChange={(e) => setTier(i, 'threshold', e.target.value)} style={{ width: '100%' }} />
                </Field>
                <Field label={i === 0 ? 'Rate (%)' : ''}>
                  <Input type="number" aria-label={`Tier ${i + 1} rate`} value={r.rate} onChange={(e) => setTier(i, 'rate', e.target.value)} placeholder="e.g. 15" style={{ width: '100%' }} />
                </Field>
                <Button variant="secondary" size="sm" aria-label={`Remove tier ${i + 1}`} disabled={tierRows.length === 1} onClick={() => setTierRows((rows) => rows.filter((_, j) => j !== i))}>Remove</Button>
              </div>
            ))}
            <div>
              <Button variant="secondary" size="sm" onClick={() => setTierRows((rows) => [...rows, { threshold: '', rate: '' }])}>Add tier</Button>
            </div>
            {tierProblem && <span role="alert" style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-status-red)' }}>{tierProblem}</span>}
            <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
              The tier the {TIER_BASIS_LABEL[tierBasis]} reaches sets the rate for the whole amount. Savings means what this term bills on.
            </span>
          </div>
        )}
        <Field label="Authority value ($, optional)">
          <Input type="number" value={authorityValue} onChange={(e) => setAuthorityValue(e.target.value)} placeholder="Two-step: a settlement below this earns beyond the cap" style={{ width: '100%' }} />
        </Field>
        {authorityValue.trim() !== '' && (
          <Field label="Rate below authority (%, optional)">
            <Input type="number" value={authorityRatePct} onChange={(e) => setAuthorityRatePct(e.target.value)} placeholder="Leave blank to use the term rate" style={{ width: '100%' }} />
          </Field>
        )}
        <Field label="Base fee ($, optional)">
          <Input type="number" value={baseFee} onChange={(e) => setBaseFee(e.target.value)} placeholder="Drafted once per property and year, never split" style={{ width: '100%' }} />
        </Field>
        <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
          Changing the rate, tiers or authority value recomputes staged billable items for this client (contingency cascade).
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={save.isPending}>Cancel</Button>
          <Button variant="primary" size="sm" loading={save.isPending} disabled={!!tierProblem} onClick={() => save.mutate()}>Save</Button>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)', fontWeight: 'var(--fw-medium)' }}>{label}</span>
      {children}
    </label>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)',
      padding: 'var(--sp-2) 0', fontSize: 'var(--fs-sm)', borderBottom: '1px solid var(--color-border)',
    }}>
      <span style={{ color: 'var(--color-ink-3)' }}>{label}</span>
      <span style={{ color: 'var(--color-ink)', fontWeight: 'var(--fw-medium)', textAlign: 'right' }}>{value}</span>
    </div>
  );
}
